import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';

import { useCategories } from '../../features/products/api';
import { cartCount, useCart } from '../../features/cart/store';
import { useWishlist } from '../../features/wishlist/store';
import { useAuth } from '../../features/auth/store';
import { StorePickup } from '../../features/pickup/StorePickup';
import { SearchBar } from './SearchBar';
import { BrandLogo } from './BrandLogo';

/**
 * Sticky top navigation (SRS §4.2): logo, search with autocomplete, category links,
 * and account/cart/wishlist icons with live counts. Opening the cart icon reveals
 * the slide-out drawer (mounted once in Layout).
 */
export function Header(): JSX.Element {
  const { data: categories = [] } = useCategories();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  const count = useCart(cartCount);
  const openCart = useCart((s) => s.open);
  const wishlistCount = useWishlist((s) => s.ids.size);

  return (
    <header className="sticky top-0 z-30 border-b border-frost bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2.5 md:gap-7 md:px-6 md:py-4">
        <Link
          to="/"
          className="flex shrink-0 items-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-ink"
          aria-label="SkyTech Solutions home"
        >
          <BrandLogo />
        </Link>

        <div className="hidden flex-1 md:block">
          <SearchBar />
        </div>

        <nav className="ml-auto flex items-center gap-1" aria-label="Account">
          <div className="hidden sm:block">
            <IconLink to="/wishlist" label="Wishlist" badge={wishlistCount || undefined}>
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                d="M10 17.5l-1.1-1C4.9 12.9 2.5 10.7 2.5 8 2.5 5.9 4.1 4.3 6.2 4.3c1.2 0 2.3.5 3 1.4.7-.9 1.8-1.4 3-1.4 2.1 0 3.7 1.6 3.7 3.7 0 2.7-2.4 4.9-6.4 8.5z"
              />
            </IconLink>
          </div>
          <button
            type="button"
            onClick={openCart}
            aria-label={`Cart, ${count} item${count === 1 ? '' : 's'}`}
            className="tap-target relative flex items-center justify-center rounded-full text-navy-700 transition-colors hover:bg-cloud hover:text-sky-ink"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden
            >
              <path d="M6 6h11l-1 7H7L6 6zm0 0L5.2 3H3m4 15a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z" />
            </svg>
            {count > 0 && <CountBadge value={count} />}
          </button>
          <div className="hidden sm:block">
            <AccountMenu />
          </div>
          <button
            type="button"
            className="tap-target ml-1 flex items-center justify-center rounded-md text-navy-700 md:hidden"
            aria-label="Menu"
            aria-controls="mobile-navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg width="22" height="22" viewBox="0 0 20 20" aria-hidden>
              <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </button>
        </nav>
      </div>

      <nav
        aria-label="Categories"
        className="mx-auto hidden max-w-7xl items-center gap-1 border-t border-frost px-6 md:flex"
      >
        <CategoryLink to="/products">All products</CategoryLink>
        {categories.map((c) => (
          <CategoryLink key={c.id} to={`/category/${c.slug}`}>
            {c.name}
          </CategoryLink>
        ))}
        <div className="ml-auto shrink-0 border-l border-frost pl-3">
          <StorePickup compact />
        </div>
      </nav>

      <div className="px-4 pb-3 md:hidden">
        <SearchBar onNavigate={() => setMobileOpen(false)} />
      </div>

      {mobileOpen && (
        <div
          id="mobile-navigation"
          className="max-h-[65dvh] overflow-y-auto border-t border-frost px-4 py-3 md:hidden"
        >
          <div className="flex flex-wrap gap-4">
            <StorePickup />
            <AccountMenu />
            <Link
              to="/wishlist"
              onClick={() => setMobileOpen(false)}
              className="tap-target inline-flex items-center text-sm text-sky-ink"
            >
              Wishlist
            </Link>
          </div>
          <nav aria-label="Categories" className="mt-3 flex flex-col">
            <CategoryLink to="/products" onClick={() => setMobileOpen(false)}>
              All products
            </CategoryLink>
            {categories.map((c) => (
              <CategoryLink
                key={c.id}
                to={`/category/${c.slug}`}
                onClick={() => setMobileOpen(false)}
              >
                {c.name}
              </CategoryLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

function AccountMenu(): JSX.Element {
  const navigate = useNavigate();
  const status = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [open, setOpen] = useState(false);

  if (status !== 'authenticated') {
    return (
      <IconLink to="/login" label="Sign in">
        <path
          d="M10 10a3 3 0 100-6 3 3 0 000 6zm-6 7c0-3 2.7-5 6-5s6 2 6 5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </IconLink>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        className="tap-target flex items-center justify-center rounded-md text-sky-ink"
      >
        <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            d="M10 10a3 3 0 100-6 3 3 0 000 6zm-6 7c0-3 2.7-5 6-5s6 2 6 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-md border border-frost bg-white py-1 shadow-hover">
            <p className="truncate px-4 py-2 text-xs text-neutral">{user?.email}</p>
            <MenuItem
              onClick={() => {
                setOpen(false);
                navigate('/orders');
              }}
            >
              Orders
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpen(false);
                navigate('/wishlist');
              }}
            >
              Wishlist
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate('/');
              }}
            >
              Sign out
            </MenuItem>
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-4 py-2 text-left text-sm text-navy-700 hover:bg-cloud"
    >
      {children}
    </button>
  );
}

function CountBadge({ value }: { value: number }): JSX.Element {
  // Keyed by value so it remounts and replays `badge-pulse` whenever the count changes
  // (SRS v2 App. H "Add to Cart Flow" — 1.0 → 1.3 → 1.0 scale on add).
  return (
    <span
      key={value}
      className="badge-pulse absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-600 px-1 text-[10px] font-medium text-white"
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

function IconLink({
  to,
  label,
  badge,
  children,
}: {
  to: string;
  label: string;
  badge?: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={to}
      aria-label={label}
      className="tap-target relative flex items-center justify-center rounded-full text-navy-700 transition-colors hover:bg-cloud hover:text-sky-ink"
    >
      <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        {children}
      </svg>
      {badge ? <CountBadge value={badge} /> : null}
    </Link>
  );
}

function CategoryLink({
  to,
  children,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex min-h-11 items-center rounded-md px-3 py-2 text-sm transition-colors hover:bg-cloud ${isActive ? 'bg-sky-500/10 text-navy-900 font-medium' : 'text-navy-700 hover:text-sky-ink'}`
      }
    >
      {children}
    </NavLink>
  );
}
